import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { cn } from '@workspace/ui/lib/utils';

import CheckIcon from '~/components/MarketplaceIcons/CheckIcon';
import ChevronDownIcon from '~/components/MarketplaceIcons/ChevronDownIcon';
import { usePriceFormatter } from '~/hooks/usePriceFormatter';
import { INLINE_LINK_CLASS } from '~/pages/DubaiExpress/constants';
import type { ProductViewProps } from '~/pages/Product/Product';
import { convertPenceToPounds } from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  sortMultibuyLevels,
} from '~/utils/multibuy';

type Props = Pick<ProductViewProps, 'isMultiBuy' | 'currentItem' | 'count'> & {
  isLoading?: boolean;
  className?: string;
};

/** Levels shown before the list folds behind a "more" link. */
const VISIBLE_LEVELS = 4;

const ROW_CLASS =
  'grid grid-cols-[1fr_auto_auto] items-baseline gap-x-3 py-1.5 [&+&]:border-0 [&+&]:border-t [&+&]:border-[#EDEDED] [&+&]:border-solid';

/**
 * Quantity price levels of the offer as a ladder on a plate: the quantity
 * that unlocks each level, its unit price and the discount against the
 * current price. The level the cart already reaches is marked. Long ladders
 * fold after four rows.
 */
const ProductDEMultibuyLevelsView = ({
  isMultiBuy,
  currentItem,
  count,
  isLoading,
  className,
}: Props) => {
  const { t } = useTranslation();
  const formatPrice = usePriceFormatter();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isMultiBuy || !currentItem?.multibuyPriceLevels.length) {
    return null;
  }

  if (isLoading) {
    return (
      <Skeleton
        height={88}
        containerClassName={cn('block', className)}
        className="rounded-[10px]!"
      />
    );
  }

  const levels = sortMultibuyLevels(currentItem.multibuyPriceLevels);
  const basePrice = currentItem.discountPrice || currentItem.price;
  const activeLevel = getApplicableMultibuyLevel(levels, count);
  const visibleLevels = isExpanded ? levels : levels.slice(0, VISIBLE_LEVELS);
  const hiddenCount = levels.length - visibleLevels.length;

  return (
    <section
      aria-label={t('phrases:saveMore')}
      className={cn('rounded-[10px] bg-[#F7F7F7] px-3 pt-2 pb-1', className)}
    >
      <h3 className="m-0 font-medium text-[#8A8A8A] text-[12px] leading-4">
        {t('phrases:saveMore')}
      </h3>
      <div className="mt-1">
        {visibleLevels.map((level) => {
          const isActive = activeLevel?.id === level.id;
          const discount =
            basePrice > 0
              ? Math.round((1 - level.unitPrice / basePrice) * 100)
              : 0;

          return (
            <div key={level.id} className={ROW_CLASS}>
              <span
                className={cn(
                  'flex items-center gap-1 font-medium text-[14px] leading-5',
                  isActive ? 'text-[#E94E1A]' : 'text-[#141415]',
                )}
              >
                {t('productDE:multibuyFromQty', { count: level.quantity })}
                {isActive && <CheckIcon className="size-3.5 shrink-0" />}
              </span>
              <span
                className={cn(
                  'whitespace-nowrap font-semibold text-[14px] leading-5',
                  isActive ? 'text-[#E94E1A]' : 'text-[#141415]',
                )}
              >
                {t('productDE:multibuyPerItem', {
                  price: formatPrice(convertPenceToPounds(level.unitPrice)),
                })}
              </span>
              <span className="min-w-9 text-right font-medium text-[#E94E1A] text-[12px] leading-4">
                {discount > 0 ? `−${discount}%` : ''}
              </span>
            </div>
          );
        })}
      </div>
      {(hiddenCount > 0 || isExpanded) && (
        <button
          type="button"
          onClick={() => setIsExpanded((value) => !value)}
          className={cn(INLINE_LINK_CLASS, 'mt-0.5 mb-1.5 text-[12px]!')}
        >
          {isExpanded
            ? t('productDE:multibuyLess')
            : t('productDE:multibuyMore', { count: hiddenCount })}
          <ChevronDownIcon
            className={cn('size-3.5', isExpanded && 'rotate-180')}
          />
        </button>
      )}
    </section>
  );
};

export const ProductDEMultibuyLevels = observer(ProductDEMultibuyLevelsView);
